import { AxiosResponse } from 'axios'
import { TCommentPost, TCommentRead, TActivityPayload, TActivityMeta, TActivities, TActivity } from '../type/activity'
import { customErrorResponse } from '../utility/custom-error-response'
import { request, setCustomOnResponseError, resetCustomOnResponseError, CustomAxiosRequestConfig } from './axios'
import { createDownloadLink } from '../utility/download-link'
import { trimAndReplaceWhiteSpaces } from '../utility/string-format'

export const ACTIVITY = {
  get: async (search_key = '', page = 1): Promise<TActivities> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const searchParam = search_key ? `&search_key=${encodeURIComponent(search_key)}` : ''
      const {
        data: { results, max_page_size, page_number, total_count, total_pages },
      } = await request({
        url: `/tpa-activities/activities/?page_number=${page}${searchParam}`,
      })

      return {
        results,
        max_page_size,
        page_number,
        total_count,
        total_pages,
      }
    } finally {
      resetCustomOnResponseError()
    }
  },

  details: async (id: string): Promise<TActivity> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: `/tpa-activities/activity/?activity_id=${id}`,
      })

      return await data.activity
    } finally {
      resetCustomOnResponseError()
    }
  },

  getMeta: async (activity_type: string): Promise<TActivityMeta> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: `/tpa-activities/meta/?activity_type=${encodeURIComponent(activity_type)}`,
      })
      return await data
    } finally {
      resetCustomOnResponseError()
    }
  },

  getActivityTypes: async (): Promise<string[]> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: '/tpa-activities/types/',
      })
      return await data.activity_types
    } finally {
      resetCustomOnResponseError()
    }
  },

  create: async (activity: TActivityPayload) => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'post',
      url: '/tpa-activities/activity/',
      data: activity,
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },

  update: async (activity: TActivityPayload, activity_id: string) => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'put',
      url: `/tpa-activities/activity/?activity_id=${encodeURIComponent(activity_id)}`,
      data: activity,
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },

  upload: async (activity_id: string, category_name: string, document_type: string, files: File[]): Promise<AxiosResponse> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const url = `/tpa-activities/documents/upload/?activity_id=${encodeURIComponent(activity_id)}&category_name=${encodeURIComponent(
        category_name,
      )}&document_type=${encodeURIComponent(document_type)}`
      const formData = new FormData()
      files.forEach((file) => formData.append('file', file))

      return await request({
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url,
        method: 'post',
        data: formData,
        showToastError: false,
      } as CustomAxiosRequestConfig)
    } finally {
      resetCustomOnResponseError()
    }
  },

  downloadDocuments: async (activity_id: string, category_name: string, document_type: string): Promise<void> => {
    const url = `/tpa-activities/documents/download/?activity_id=${encodeURIComponent(activity_id)}&category_name=${encodeURIComponent(
      category_name,
    )}&document_type=${encodeURIComponent(document_type)}`

    const response = await request({
      responseType: 'arraybuffer',
      url,
      method: 'post',
    })

    const contentDisposition = response.headers['content-disposition']
    let fileName = `${activity_id}`

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1]
      }
    } else {
      const contentType = response.headers['content-disposition']
      const extension = contentType.split('/')[1]
      fileName = `${activity_id}.${extension}`
    }

    createDownloadLink(response.data, fileName)
  },

  downloadAllDocuments: async (activity_id: string): Promise<void> => {
    const url = `/tpa-activities/documents/download/all/?activity_id=${encodeURIComponent(activity_id)}`

    const response = await request({
      responseType: 'arraybuffer',
      url,
      method: 'post',
    })

    const contentDisposition = response.headers['content-disposition']
    let fileName = `${activity_id}`

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1]
      }
    } else {
      const contentType = response.headers['content-disposition']
      const extension = contentType.split('/')[1]
      fileName = `${activity_id}.${extension}`
    }

    createDownloadLink(response.data, fileName)
  },

  updateStatus: async (activity_id: string, category_name: string, document_type: string, status: string) => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'put',
      url: `/tpa-activities/documents/status/?activity_id=${activity_id}&category_name=${encodeURIComponent(
        category_name,
      )}&document_type=${encodeURIComponent(document_type)}&status=${status}`,
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },

  archiveDeliverable: async (activity_id: string, file_id: string) => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'delete',
      url: `/tpa-activities/deliverables/?activity_id=${activity_id}&file_id=${file_id}`,
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },
  updateDeliverable: async (activity_id: string, file_id: string, description: string) => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'put',
      url: `/tpa-activities/deliverables/?activity_id=${activity_id}&file_id=${file_id}`,
      data: { description: trimAndReplaceWhiteSpaces(description) },
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },

  archiveDocument: async (activity_id: string, file_id: string) => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'delete',
      url: `/tpa-activities/documents/?activity_id=${activity_id}&file_id=${file_id}`,
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },

  downloadDeliverables: async (activity_id: string, file_id: string, file_name: string): Promise<void> => {
    const url = `/tpa-activities/deliverables/download/?activity_id=${encodeURIComponent(activity_id)}&file_id=${encodeURIComponent(
      file_id,
    )}`

    const { data } = await request({
      responseType: 'arraybuffer',
      url,
      method: 'post',
    })
    createDownloadLink(data, file_name)
  },

  uploadDeliverables: async (file: File, activity_id: string, description?: string): Promise<AxiosResponse<any>> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      let url = `/tpa-activities/deliverables/upload/?activity_id=${encodeURIComponent(activity_id)}`
      if (description) {
        url += `&description=${encodeURIComponent(trimAndReplaceWhiteSpaces(description))}`
      }
      const formData = new FormData()
      formData.append('file', file)
      return await request({
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url,
        method: 'post',
        data: formData,
      } as CustomAxiosRequestConfig)
    } finally {
      resetCustomOnResponseError()
    }
  },

  sendComment: async (comment: TCommentPost): Promise<AxiosResponse<any>> =>
    request({
      method: 'post',
      url: '/tpa-activities/documents/comments/',
      data: comment,
      showToastSuccess: false,
    } as CustomAxiosRequestConfig),

  readComment: async (comment: TCommentRead): Promise<AxiosResponse<any>> =>
    request({
      method: 'put',
      url: '/tpa-activities/documents/comments/is_read/',
      data: comment,
      showToastError: true,
      showToastSuccess: false,
    } as CustomAxiosRequestConfig),

  changeActivityStatus: async (activity_id: string, status: 'Canceled' | 'Completed') => {
    setCustomOnResponseError(customErrorResponse)
    return request({
      method: 'put',
      url: `/tpa-activities/activity/status/?activity_id=${activity_id}&status=${status}`,
    }).finally(() => {
      resetCustomOnResponseError()
    })
  },
}
